/**
 * LocalStorage utilities for managing prompt drafts and versions
 */

import { DraftPrompt, PromptVersion, CachedEvaluationResults } from './types';

const STORAGE_PREFIX = 'ai_matching_';
const DRAFTS_KEY = (jobId: string) => `${STORAGE_PREFIX}drafts_${jobId}`;
const VERSIONS_KEY = (jobId: string) => `${STORAGE_PREFIX}versions_${jobId}`;
const RESULTS_KEY = (jobId: string, draftId: string) => `${STORAGE_PREFIX}results_${jobId}_${draftId}`;

// Draft Management
export const getDrafts = (jobId: string): DraftPrompt[] => {
    try {
        const stored = localStorage.getItem(DRAFTS_KEY(jobId));
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.error('Error loading drafts:', error);
        return [];
    }
};

export const saveDraft = (
    jobId: string,
    draft: Omit<DraftPrompt, 'id' | 'createdAt' | 'updatedAt'>,
    draftId?: string
): DraftPrompt => {
    const drafts = getDrafts(jobId);
    const now = new Date().toISOString();

    if (draftId) {
        // Update existing draft
        const index = drafts.findIndex(d => d.id === draftId);
        if (index !== -1) {
            drafts[index] = {
                ...drafts[index],
                ...draft,
                updatedAt: now,
            };
            localStorage.setItem(DRAFTS_KEY(jobId), JSON.stringify(drafts));
            return drafts[index];
        }
    }

    // Create new draft
    const newDraft: DraftPrompt = {
        ...draft,
        id: `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: now,
        updatedAt: now,
    };

    drafts.push(newDraft);
    localStorage.setItem(DRAFTS_KEY(jobId), JSON.stringify(drafts));
    return newDraft;
};

export const deleteDraft = (jobId: string, draftId: string): void => {
    const drafts = getDrafts(jobId).filter(d => d.id !== draftId);
    localStorage.setItem(DRAFTS_KEY(jobId), JSON.stringify(drafts));

    // Also delete cached results
    clearDraftResults(jobId, draftId);
};

export const renameDraft = (jobId: string, draftId: string, newName: string): void => {
    const drafts = getDrafts(jobId);
    const draft = drafts.find(d => d.id === draftId);
    if (draft) {
        draft.name = newName;
        draft.updatedAt = new Date().toISOString();
        localStorage.setItem(DRAFTS_KEY(jobId), JSON.stringify(drafts));
    }
};

// Version Management
export const getVersions = (jobId: string): PromptVersion[] => {
    try {
        const stored = localStorage.getItem(VERSIONS_KEY(jobId));
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.error('Error loading versions:', error);
        return [];
    }
};

export const saveVersion = (jobId: string, version: PromptVersion): void => {
    const versions = getVersions(jobId);
    const index = versions.findIndex(v => v.id === version.id);

    if (index !== -1) {
        versions[index] = version;
    } else {
        versions.push(version);
    }

    localStorage.setItem(VERSIONS_KEY(jobId), JSON.stringify(versions));
};

export const setActiveVersion = (jobId: string, versionId: string): void => {
    const versions = getVersions(jobId);
    versions.forEach(v => {
        v.isActive = v.id === versionId;
    });
    localStorage.setItem(VERSIONS_KEY(jobId), JSON.stringify(versions));
};

// Evaluation Results Cache
export const saveDraftResults = (
    jobId: string,
    draftId: string,
    results: CachedEvaluationResults
): void => {
    localStorage.setItem(RESULTS_KEY(jobId, draftId), JSON.stringify(results));
};

export const getDraftResults = (
    jobId: string,
    draftId: string
): CachedEvaluationResults | null => {
    try {
        const stored = localStorage.getItem(RESULTS_KEY(jobId, draftId));
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        console.error('Error loading draft results:', error);
        return null;
    }
};

export const clearDraftResults = (jobId: string, draftId: string): void => {
    localStorage.removeItem(RESULTS_KEY(jobId, draftId));
};
